import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Review {
  id: number;
  name: string;
  rating: number;
  text: string;
  date: string;
}

const reviews: Review[] = [
  {
    id: 1,
    name: 'Marco van den Berg',
    rating: 5,
    text: 'Prachtige cinewall met haard geleverd en geïnstalleerd. De kwaliteit is uitstekend en het team was zeer professioneel. Onze woonkamer is nu echt een blikvanger!',
    date: '2 weken geleden'
  },
  {
    id: 2,
    name: 'Linda Jansen',
    rating: 5,
    text: 'Fantastische service van begin tot eind. De configurator hielp ons perfect bij het maken van keuzes. Het eindresultaat overtreft onze verwachtingen. Absolute aanrader!',
    date: '1 maand geleden'
  },
  {
    id: 3,
    name: 'Robert Vermeulen',
    rating: 5,
    text: 'Zeer tevreden met onze nieuwe cinewall. De elektrische haard geeft een prachtige sfeer en de LED verlichting maakt het helemaal af. Top kwaliteit voor een eerlijke prijs.',
    date: '3 weken geleden'
  },
  {
    id: 4,
    name: 'Sophie de Vries',
    rating: 5,
    text: 'Professionele installatie en prachtig eindresultaat. De cinewall is precies zoals we hadden gewenst. Het advies vooraf was uitstekend en de monteurs werkten zeer netjes.',
    date: '1 maand geleden'
  },
  {
    id: 5,
    name: 'Thomas Bakker',
    rating: 5,
    text: 'Geweldige ervaring! Van ontwerp tot installatie alles perfect geregeld. De cinewall met haard is echt een eyecatcher geworden in ons huis. Zeer aan te bevelen!',
    date: '2 maanden geleden'
  },
  {
    id: 6,
    name: 'Emma Peters',
    rating: 5,
    text: 'Uitstekende kwaliteit en service. De cinewall ziet er luxe uit en de haard werkt perfect. Het team dacht goed mee en leverde precies wat we zochten.',
    date: '3 weken geleden'
  }
];

export default function GoogleReviews() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const reviewsPerView = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 3;

  const nextReview = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      nextReview();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentIndex]);

  const getVisibleReviews = () => {
    const visible = [];
    for (let i = 0; i < reviewsPerView; i++) {
      visible.push(reviews[(currentIndex + i) % reviews.length]);
    }
    return visible;
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -1000 : 1000,
      opacity: 0
    })
  };

  return (
    <section className="py-24 bg-gradient-to-b from-zinc-900 to-black">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 mb-6"
          >
            <i className="ri-google-fill text-4xl text-white"></i>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-2xl text-amber-400"></i>
              ))}
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-light text-white mb-4"
          >
            Wat onze klanten zeggen
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400"
          >
            Uitstekend – <span className="text-amber-400 font-medium">4,9/5</span> op basis van{' '}
            <span className="text-white font-medium">219 Google reviews</span>
          </motion.p>
        </div>

        {/* Reviews Carousel */}
        <div className="relative">
          <div 
            className="overflow-hidden"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
              >
                {getVisibleReviews().map((review) => (
                  <div
                    key={review.id}
                    className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300"
                  >
                    {/* Stars */}
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-xl text-amber-400"></i>
                      ))}
                    </div>

                    {/* Review Text */}
                    <p className="text-zinc-700 text-base leading-relaxed mb-6 min-h-[120px]">
                      "{review.text}"
                    </p>

                    {/* Author */}
                    <div className="flex items-center justify-between pt-4 border-t border-zinc-200">
                      <div>
                        <p className="font-medium text-zinc-900">{review.name}</p>
                        <p className="text-sm text-zinc-500">{review.date}</p>
                      </div>
                      <i className="ri-google-fill text-2xl text-zinc-400"></i>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevReview}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-zinc-100 transition-colors cursor-pointer group"
            aria-label="Vorige review"
          >
            <i className="ri-arrow-left-s-line text-2xl text-zinc-900 group-hover:text-amber-600 transition-colors"></i>
          </button>

          <button
            onClick={nextReview}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-zinc-100 transition-colors cursor-pointer group"
            aria-label="Volgende review"
          >
            <i className="ri-arrow-right-s-line text-2xl text-zinc-900 group-hover:text-amber-600 transition-colors"></i>
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex items-center justify-center gap-2 mt-12">
          {reviews.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentIndex
                  ? 'bg-amber-400 w-8'
                  : 'bg-zinc-600 hover:bg-zinc-500'
              }`}
              aria-label={`Ga naar review ${index + 1}`}
            />
          ))}
        </div>

        {/* View All Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="https://www.google.com/search?q=diamondflame.nl+reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white hover:text-amber-400 transition-colors text-lg group cursor-pointer"
          >
            <span>Bekijk alle Google reviews</span>
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
